import { useEffect, useState } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRHubStore } from '../stores/useSignalR';

export type SignalRStatus = 'connected' | 'reconnecting' | 'disconnected' | 'idle';

// Collapse every hub's connection into a single status:
//  - connected     : at least one hub is live
//  - reconnecting  : a hub is reconnecting / still establishing (and none is live)
//  - disconnected  : hubs exist but all are down
//  - idle          : no hub has been initialized (e.g. a page with no live stream)
const aggregate = (states: Array<HubConnectionState | undefined>): SignalRStatus => {
  const known = states.filter((s): s is HubConnectionState => s !== undefined);
  if (known.some((s) => s === HubConnectionState.Connected)) return 'connected';
  if (known.some((s) => s === HubConnectionState.Reconnecting || s === HubConnectionState.Connecting))
    return 'reconnecting';
  if (known.length > 0) return 'disconnected';
  return 'idle';
};

/**
 * Reactive SignalR connection status. The HubConnection mutates its own `.state` internally,
 * which zustand cannot observe, so we poll the store's connections on a short interval.
 */
export const useSignalRStatus = (pollMs = 1000): SignalRStatus => {
  const [status, setStatus] = useState<SignalRStatus>('idle');

  useEffect(() => {
    const read = () =>
      setStatus(
        aggregate(
          Object.values(useSignalRHubStore.getState().hubs).map((h) => h?.connection?.state),
        ),
      );

    read();
    const id = setInterval(read, pollMs);
    return () => clearInterval(id);
  }, [pollMs]);

  return status;
};
